import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const menuItems = [
    { label: "Accueil", id: "hero" },
    { label: "Le Gîte", id: "presentation" },
    { label: "Jardin", id: "jardin" },
    { label: "Équipements", id: "amenities" },
    { label: "Activités", id: "activities" },
    { label: "Tarifs", id: "pricing" },
    { label: "Avis", id: "reviews" },
    { label: "Contact", id: "contact-form" },
  ];

  const navText = isScrolled ? "text-[#1C3A27]" : "text-white";
  const navHover = isScrolled ? "hover:text-[#2A5236]" : "hover:text-white";
  const navShadow = isScrolled ? "" : "[text-shadow:_0_1px_10px_rgba(0,0,0,0.4)]";

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-lg py-2 md:py-4" : "bg-transparent py-4 md:py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav role="navigation" className="flex items-center justify-between">
          <div
            className={`font-serif text-xl md:text-2xl font-bold ${navText} ${navHover} ${navShadow} cursor-pointer transition-colors`}
            onClick={() => scrollToSection("hero")}
            role="button"
            tabIndex={0}
            aria-label="Retour à l'accueil"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") scrollToSection("hero");
            }}
          >
            Cœurs de Normands
          </div>

          {/* Menu Desktop */}
          <div className="hidden lg:flex items-center space-x-5 xl:space-x-7">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`${navText} ${navHover} ${navShadow} font-medium transition-colors duration-200 text-sm xl:text-base focus:outline-none focus:ring-2 focus:ring-white/70 focus:ring-offset-0 rounded-md px-1.5 py-1`}
                aria-label={`Aller à la section ${item.label}`}
              >
                {item.label}
              </button>
            ))}

            <div className={`flex items-center space-x-2 border-l ${isScrolled ? "border-[#EDE6DB]" : "border-white/30"} pl-4`}>
              <button
                className={`flex items-center space-x-1 px-2 py-1 rounded-md text-sm font-medium ${navText} ${navShadow} hover:bg-white/10 transition-colors`}
                title="Français"
              >
                <span>🇫🇷</span>
                <span>FR</span>
              </button>
              <button
                className={`flex items-center space-x-1 px-2 py-1 rounded-md text-sm font-medium ${isScrolled ? "text-[#1C3A27]/50 hover:text-[#1C3A27]" : "text-white/70 hover:text-white"} ${navShadow} hover:bg-white/10 transition-colors`}
                title="English"
                onClick={() => (window.location.href = "/en")}
              >
                <span>🇬🇧</span>
                <span>EN</span>
              </button>
            </div>

            <button
              onClick={() => scrollToSection("contact-form")}
              className="btn-primary text-sm xl:text-base shadow-lg focus:outline-none focus:ring-2 focus:ring-white/70"
              aria-label="Aller au formulaire de demande de séjour"
              title="Vérifier les disponibilités"
            >
              Réserver
            </button>
          </div>

          {/* Bouton Menu Mobile */}
          <button
            className={`lg:hidden p-2 ${navText} ${navHover} transition-colors focus:outline-none focus:ring-2 focus:ring-white/70 rounded-md`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>

        {/* Menu Mobile */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t border-[#EDE6DB] bg-white/95 backdrop-blur-sm rounded-b-lg shadow-lg">
            <div className="flex flex-col space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-[#1C3A27] hover:text-[#2A5236] font-medium text-left transition-colors duration-200 px-3 py-3 rounded-md hover:bg-[#EDE6DB]/30 focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2"
                  aria-label={`Aller à la section ${item.label}`}
                >
                  {item.label}
                </button>
              ))}

              <div className="flex items-center space-x-2 px-3 py-2 border-t border-[#EDE6DB]/50 mt-2">
                <span className="text-sm text-[#1C3A27]/70 font-medium">Langue :</span>
                <button
                  className="flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium bg-[#1C3A27] text-white"
                  title="Français"
                >
                  <span>🇫🇷</span>
                  <span>FR</span>
                </button>
                <button
                  className="flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium text-[#1C3A27] border border-[#EDE6DB] hover:bg-[#EDE6DB]/30 transition-colors"
                  title="English"
                  onClick={() => (window.location.href = "/en")}
                >
                  <span>🇬🇧</span>
                  <span>EN</span>
                </button>
              </div>

              <button
                onClick={() => scrollToSection("contact-form")}
                className="btn-primary w-fit mt-2 mx-3 focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2"
                aria-label="Aller au formulaire de demande de séjour"
              >
                Réserver
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
